<template>
	<card title="Details" class="breakdown" layout="table" v-if="hasData">
		<template #default>
			<div class="columns">
				<div class="column is-6">
					<div class="is-flex flex-wrap is-justify-content-flex-start">
						<div class="is-breadcrumb"><a>Global</a></div>
					</div>
				</div>
				<div class="column is-6 has-text-right is-flex is-justify-content-flex-end">
					<div class="mr-4">
						<o-tooltip>
							<o-input
								placeholder="Search..."
								type="search"
								icon="search"
								expanded
								disabled></o-input>
							<template #content>Content is loading please wait..</template>
						</o-tooltip>
					</div>
					<o-tooltip>
						<o-select v-model="view" placeholder="Region" disabled>
							<option :key="option.key" :value="option.key" v-for="option in options.breakdowns">
								{{ option.text }}
							</option>
						</o-select>
						<template #content>Content is loading please wait..</template>
					</o-tooltip>
				</div>
			</div>
			<div class="tab-section mb-5">
				<o-tabs v-model="view" :multiline="true" :expanded="true">
					<o-tab-item value="region" label="Region" :disabled="!HasView('region')"></o-tab-item>
					<o-tab-item value="license" label="License" :disabled="!HasView('license')"></o-tab-item>
					<o-tab-item
						value="location"
						label="Location"
						:disabled="!HasView('location')"></o-tab-item>
					<o-tab-item
						value="fullName"
						label="Partner"
						:disabled="!HasView('fullName')"></o-tab-item>
					<o-tab-item
						value="assignmentId"
						label="Assignment"
						:disabled="!HasView('assignmentId')"></o-tab-item>
					<o-tab-item value="company" label="Company" :disabled="!HasView('company')"></o-tab-item>
					<o-tab-item value="parent" label="Parent" :disabled="!HasView('parent')"></o-tab-item>
					<o-tab-item
						value="function"
						label="Function"
						:disabled="!HasView('function')"></o-tab-item>
					<o-tab-item
						value="industry"
						label="Industry"
						:disabled="!HasView('industry')"></o-tab-item>
				<o-tab-item
					value="reportGroup"
					label="Report Group"
					:disabled="true"></o-tab-item>
				<o-tab-item
					value="industryGroup"
					label="Practice Group"
					:disabled="true"></o-tab-item>
				<o-tab-item
					value="sector"
					label="Sector"
					:disabled="true"></o-tab-item>
			</o-tabs>
			<o-notification
				variant="warning is-light"
				v-if="view == 'reportGroup' || view == 'practiceGroup' || view == 'subsector' || view == 'industryGroup' || view == 'sector' || view == 'capability'">
					Industry and function codes may be shared across multiple groups. Therefore the sum of the
					subtotals below may exceed the Total Billings shown above.
				</o-notification>
			</div>

			<div>
				<o-table
					class="table-full-width"
					paginated
					:per-page="100"
					pagination-simple
					:data="results"
					:default-sort="['totalBillings', 'desc']">
					<o-table-column label="Name" field="name" sortable v-slot="props">
						<span v-if="view == 'assignmentId'">
							<a :href="'/mcl/assignments/' + props.row.name" target="_blank">
								{{ props.row.name }}
							</a>
						</span>
						<span v-else-if="view == 'company'">
							<a :href="'/mcl/companies/' + props.row.companyId" target="_blank">
								{{ props.row.name }}
							</a>
						</span>
						<span v-else-if="view == 'parent'">
							<a :href="'/mcl/companies/' + props.row.parentId" target="_blank">
								{{ props.row.name }}
							</a>
						</span>
						<span
							v-else-if="
								view == 'region' || view == 'license' || view == 'location' || view == 'fullName'
							">
							<o-tooltip>
								<a href="javascript://">
									{{ props.row.name }}
								</a>
								<template #content>Content is loading please wait..</template>
							</o-tooltip>
						</span>
						<span v-else>
							{{ props.row.name }}
						</span>
					</o-table-column>
					<o-table-column label="Total Billings" field="totalBillings" sortable v-slot="props">
						{{ $filters.Currency($store.getters.user.Currency, props.row.totalBillings) }}
					</o-table-column>
					<o-table-column label="Teaming Value" field="teamingValue" sortable v-slot="props">
						{{ $filters.Currency($store.getters.user.Currency, props.row.teamingValue) }}
					</o-table-column>
					<o-table-column label="Teaming Revenue" field="teamingRevenue" sortable v-slot="props">
						{{ $filters.Currency($store.getters.user.Currency, props.row.teamingRevenue) }}
					</o-table-column>
					<o-table-column label="Teaming Taken" field="teamingTaken" sortable v-slot="props">
						{{ nformat(props.row.teamingTaken, 2) }}
					</o-table-column>
					<o-table-column label="Teaming Filled" field="teamingFilled" sortable v-slot="props">
						{{ nformat(props.row.teamingFilled, 2) }}
					</o-table-column>
					<o-table-column
						label="Assignments Taken"
						field="assignmentsTaken"
						sortable
						v-slot="props">
						{{ nformat(props.row.assignmentsTaken, 2) }}
					</o-table-column>
					<o-table-column
						label="Assignments Filled"
						field="assignmentsFilled"
						sortable
						v-slot="props">
						{{ nformat(props.row.assignmentsFilled, 2) }}
					</o-table-column>
					<o-table-column label="Time to Fill" field="timeToFill" sortable v-slot="props">
						{{ nformat(props.row.timeToFill, 2) }}
					</o-table-column>
					<o-table-column
						label="Average Filled Job Value"
						field="filledJobValue"
						sortable
						v-slot="props"
						v-if="showFilledJobValue">
						{{ $filters.Currency($store.getters.user.Currency, props.row.filledJobValue) }}
					</o-table-column>
				</o-table>
			</div>
		</template>
	</card>
</template>
<script>
	import { ref, inject, computed, onMounted, onBeforeMount, watch } from "vue";
	import { GenericReport, Breakdown, BreakdownList } from "@/assets/types";
	import { currencyFormat, numberFormat } from "@/services/utils";
	import store from "@/store";

	export default {
		props: {
			data: {
				type: Object,
				default: () => {
					return {};
				}
			},
			showFilledJobValue: {
				type: Boolean,
				default: false
			}
		},
		setup(props, context) {
			const cformat = ref(currencyFormat.format);
			const nformat = ref(numberFormat.format);

			const view = ref("region");
			const options = ref({
				breakdowns: BreakdownList
			});

			const HasView = (v) => {
				return props.data && !!props.data[v];
			};

			const hasData = computed(() => {
				return props.data && Object.keys(props.data).length > 0;
			});

			const results = computed(() => {
				return props.data[view.value];
			});

			return {
				cformat,
				nformat,
				options,
				view,
				results,
				hasData,
				HasView
			};
		}
	};
</script>